import React from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { DocumentItem } from './documentItem';
import DocumentPickerList from './DocumentPickerList';

interface DocumentPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: DocumentItem[];
  selectedDocumentId?: string;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  loading: boolean;
  loadingMore: boolean;
  totalCount: number;
  canLoadMore: boolean;
  contentLoading: boolean;
  fetchError: string | null;
  onLoadMore: () => void;
  onSelect: (doc: DocumentItem) => void;
  onCancel: () => void;
  onRetry?: () => void;
}

const DocumentPickerDialog: React.FC<DocumentPickerDialogProps> = ({
  open,
  onOpenChange,
  documents,
  selectedDocumentId,
  searchQuery,
  onSearchQueryChange,
  loading,
  loadingMore,
  totalCount,
  canLoadMore,
  contentLoading,
  fetchError,
  onLoadMore,
  onSelect,
  onCancel,
  onRetry,
}) => {
  const { t } = useTranslation();
  const searchHelpId = React.useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="revy-ui drbfm-document-picker h-[554px] max-h-[85dvh] rounded-[10px] sm:rounded-[10px] bg-[#f8fafc] dark:bg-background grid-rows-[auto_minmax(0,1fr)_auto] gap-0 p-0 sm:max-w-[600px]">
        <DialogHeader className="p-6">
          <DialogTitle className="revy-cjk">
            {t('drbfm.documentSelector.selectDocument', 'ドキュメントを選択')}
          </DialogTitle>
        </DialogHeader>

        <div className="relative flex min-h-0 flex-col border-y">
          <div className="shrink-0 space-y-1.5 px-6 pb-4 pt-6">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                strokeWidth={1.9}
                aria-hidden="true"
              />
              <Input
                className="revy-cjk h-8 pl-9 pr-9 text-sm focus-visible:border-[#0084ff] focus-visible:ring-2 focus-visible:ring-[#0084ff]"
                placeholder={t('drbfm.documentSelector.searchPlaceholder', 'ドキュメントを検索...')}
                aria-describedby={searchHelpId}
                aria-label={t('drbfm.documentSelector.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.clear')}
                  onClick={() => onSearchQueryChange('')}
                  className="absolute right-0.5 top-1/2 -translate-y-1/2"
                >
                  <X strokeWidth={1.9} />
                </Button>
              )}
            </div>
            <p id={searchHelpId} className="revy-cjk text-xs leading-[1.5] text-muted-foreground">
              {t(
                'drbfm.documentSelector.searchHelp',
                '登録済み文書をタイトル、説明、ファイル名、カテゴリ、タグから検索できます。',
              )}
            </p>
            {!loading && documents.length > 0 && (
              <p className="revy-cjk pt-3 text-xs text-muted-foreground">
                {t('drbfm.documentSelector.resultsCount', {
                  shown: documents.length,
                  total: totalCount,
                })}
              </p>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
            {fetchError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription className="revy-cjk leading-[1.6]">{fetchError}</AlertDescription>
                {onRetry && <Button variant="outline" onClick={onRetry}>{t('common.retry')}</Button>}
              </Alert>
            )}

            {loading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : fetchError && documents.length === 0 ? null : documents.length === 0 ? (
              <div className="py-8 text-center">
                <p className="revy-cjk text-sm leading-[1.5] text-muted-foreground">
                  {searchQuery
                    ? t('drbfm.documentSelector.noResults', '検索結果がありません')
                    : t('drbfm.documentSelector.noDocuments', 'ドキュメントがありません')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <DocumentPickerList
                  documents={documents}
                  selectedDocumentId={selectedDocumentId}
                  disabled={contentLoading}
                  onSelect={onSelect}
                />
                {canLoadMore && (
                  <div className="flex justify-center pt-1">
                    <Button variant="outline" onClick={onLoadMore} disabled={loadingMore}>
                      {loadingMore && <Spinner size="sm" label="" />}
                      {loadingMore
                        ? t('drbfm.documentSelector.loadingMore')
                        : t('drbfm.documentSelector.loadMore')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {contentLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
              <div className="text-center">
                <Spinner size="lg" label="" className="mx-auto" />
                <p className="revy-cjk mt-2 text-sm leading-[1.5] text-muted-foreground">
                  {t('drbfm.documentSelector.loadingContent', 'コンテンツを読み込み中...')}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-6">
          <Button variant="outline" onClick={onCancel}>
            <span className="revy-cjk">{t('common.cancel', 'キャンセル')}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentPickerDialog;
